import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Container, Row, Col, Spinner } from 'react-bootstrap';
import axios from 'axios';

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    oldPassword: '',
    password: '',
    confirmPassword: '',
    role: '',
    doctorId: '',
    isActive: true
  });
  const [validated, setValidated] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const { username, email, oldPassword, password, confirmPassword, role, doctorId, isActive } = formData;
  
  // Load user data and doctors list
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user data
        const userRes = await axios.get(`/api/users.php?id=${id}`);
        
        // Fetch doctors for doctor role
        const doctorsRes = await axios.get('/api/doctors.php');
        
        setDoctors(doctorsRes.data);
        
        // Set form data from user
        setFormData({
          username: userRes.data.username || '',
          email: userRes.data.email || '',
          oldPassword: '',
          password: '',
          confirmPassword: '',
          role: userRes.data.role || 'receptionist',
          doctorId: userRes.data.doctorId ? String(userRes.data.doctorId) : '',
          isActive: userRes.data.isActive
        });
        
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || 'Error loading user data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // Check password match
    if (e.target.name === 'confirmPassword' || e.target.name === 'password') {
      if (e.target.name === 'confirmPassword') {
        setPasswordMatch(e.target.value === password || e.target.value === '');
      } else {
        setPasswordMatch(confirmPassword === e.target.value || confirmPassword === '');
      }
    }
    
    // Clear success message on change
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError(null);
    
    const form = e.currentTarget;
    if (form && form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    // If changing password, verify old password first
    if (password) {
      if (!oldPassword) {
        setError('Please enter old password to change password');
        return;
      }
      
      if (password !== confirmPassword) {
        setPasswordMatch(false);
        setError('Passwords do not match');
        return;
      }
      
      if (password.length < 4) {
        setError('Password must be at least 4 characters');
        return;
      }
      
      // Verify old password
      try {
        await axios.post('/api/login.php', {
          username: username,
          password: oldPassword,
          loginType: 'staff'
        });
      } catch (loginErr) {
        setError('Old password is incorrect!');
        return;
      }
    }
    
    const userData = {
      username,
      email,
      role,
      isActive
    };
    
    if (password) {
      userData.password = password;
    }
    
    if (role === 'doctor') {
      userData.doctorId = doctorId;
    }
    
    try {
      await axios.put(`/api/users.php?id=${id}`, { ...userData, id: parseInt(id) });
      setSuccessMessage('User updated successfully');
      setFormData({ ...formData, oldPassword: '', password: '', confirmPassword: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating user');
    }
  };
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }
  
  return (
    <Container>
      <Row className="justify-content-center mt-5">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-4">
                <Button 
                  variant="outline-primary" 
                  className="me-3" 
                  onClick={() => navigate('/users')}
                  size="sm"
                >
                  &larr; Back to Users
                </Button>
                <h2 className="m-0">Edit User</h2>
              </div>
              
              {error && <Alert variant="danger">{error}</Alert>}
              {successMessage && <Alert variant="success">{successMessage}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={onSubmit} autoComplete="off">
                <Row>
                  <Col>
                    <Form.Group controlId="username" className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter username"
                        name="username"
                        value={username}
                        onChange={onChange}
                        required
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a username.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group controlId="email" className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        name="email"
                        value={email}
                        onChange={onChange}
                        required
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <hr className="my-4" />
                    <h5>Change Password (Optional)</h5>
                    
                    <Form.Group controlId="oldPassword" className="mb-3">
                      <Form.Label>Old Password *</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter current password"
                        name="oldPassword"
                        value={oldPassword}
                        onChange={onChange}
                        autoComplete="new-password"
                      />
                      <Form.Text className="text-muted">
                        Required to change password
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group controlId="password" className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password (min 4 characters)"
                        name="password"
                        value={password}
                        onChange={onChange}
                        minLength={4}
                        autoComplete="new-password"
                      />
                    </Form.Group>
                    
                    <Form.Group controlId="confirmPassword" className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm new password"
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={onChange}
                        isInvalid={!passwordMatch && confirmPassword !== ''}
                        autoComplete="new-password"
                      />
                      <Form.Control.Feedback type="invalid">
                        Passwords do not match.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <hr className="my-4" />
                    
                    <Form.Group controlId="role" className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={role}
                        onChange={onChange}
                        required
                      >
                        <option value="receptionist">Receptionist</option>
                        <option value="doctor">Doctor</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </Form.Group>
                    
                    {role === 'doctor' && (
                      <Form.Group controlId="doctorId" className="mb-3">
                        <Form.Label>Select Doctor</Form.Label>
                        <Form.Select
                          name="doctorId"
                          value={doctorId}
                          onChange={onChange}
                          required={role === 'doctor'}
                        >
                          <option value="">Select a doctor...</option>
                          {doctors.map(doctor => (
                            <option key={doctor.id} value={doctor.id}>
                              {doctor.name} - {doctor.specialization}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Please select a doctor.
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                    
                    <Form.Group controlId="isActive" className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Account Active"
                        name="isActive"
                        checked={isActive}
                        onChange={handleCheckboxChange}
                      />
                      <Form.Text className="text-muted">
                        Inactive accounts cannot log in
                      </Form.Text>
                    </Form.Group>
                    
                    <Button variant="primary" type="submit" className="mt-3">
                      Update User
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EditUser; 
